import React, { useEffect, useRef, useState } from 'react';
import {
  Users, QrCode, Download, LogOut, ChevronDown, ChevronsUp, ChevronsDown, Plus,
  FileText, Table, GraduationCap, Settings, Camera, ClipboardCheck, BarChart3, Archive,
  PlayCircle, BookOpen, RotateCcw, XCircle, Lock, Check, Eye, Shuffle, MoreHorizontal, Monitor,
} from 'lucide-react';
import { stepIndex } from '../utils/deriveSession';
import { ui } from '../ui';

const VIEW_TABS = [
  { id: 'history', label: 'History' },
  { id: 'physical', label: 'Physical' },
  { id: 'differential', label: 'Differential' },
  { id: 'labs', label: 'Labs' },
  { id: 'cdm', label: 'CDM' },
];

function Dropdown({ open, children }) {
  if (!open) return null;
  return <div className={ui.menu}>{children}</div>;
}

const MenuItem = ({ icon: Icon, label, onClick, tone = '' }) => (
  <button onClick={onClick} className={`${ui.menuItem} ${tone}`}>
    {Icon && <Icon className="w-4 h-4 text-ink-2" />}
    {label}
  </button>
);

/**
 * Two rows, sticky: a 48 px bar (identity, session, quiet actions, the one
 * filled Advance button), then the step tabs, which double as the progress
 * stepper. The guided case card renders as `children` beneath them and
 * compresses on scroll (see CaseFlowPanel).
 *
 * Collapsed, or in presenter mode, the bar is hidden and only the tabs stay.
 * On a phone the bar shrinks to the app name, the online count and an
 * overflow menu that holds every action.
 */
export default function Header({
  currentView, setCurrentView, userName, sessionId, onlineCount, connected,
  spectator = false, isFacilitator = false, budget = null, // {spent, total} in budget mode
  caseFlow = { active: false },
  action = null, // the case flow's one contextual action for this step
  actionQuiet = false, // outline instead of filled (individual phase: Merge & share is the one filled control)
  onExportPdf, onExportCsv, onAddRow, onAddCriticalRow, onAddDiffRow,
  onInvite, onLeave,
  onOpenSettings, onSnapshot, onOpenChecklist, onOpenAnalytics, onOpenArchive, onOpenLoadCase,
  onOpenCaseLibrary, onResetCase, onEndCase,
  presenter = false, onTogglePresenter,
  collapsed = false, onToggleCollapse,
  children,
}) {
  const [openMenu, setOpenMenu] = useState(null); // 'add' | 'export' | 'facilitator' | 'more'
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (name) => setOpenMenu(openMenu === name ? null : name);
  const closeAnd = (fn) => () => { setOpenMenu(null); fn && fn(); };

  // The tab strip doubles as the progress stepper.
  const tabState = (view) => {
    if (!caseFlow.active) return 'open';
    const at = stepIndex(view);
    const current = stepIndex(caseFlow.step);
    if (caseFlow.completed?.[view] || at < current) return 'done';
    if (at === current) return 'current';
    return 'future';
  };
  // Students can't open steps the class hasn't reached; the facilitator can.
  const isTabLocked = (view) => tabState(view) === 'future' && !isFacilitator;

  const tabs = (
    <div className="flex items-stretch gap-0.5 min-w-0 overflow-x-auto flex-1" role="tablist">
      {VIEW_TABS.map((tab) => {
        const state = tabState(tab.id);
        const locked = isTabLocked(tab.id);
        const selected = currentView === tab.id;
        const tone = selected
          ? 'text-accent font-medium'
          : locked
            ? 'text-ink-2/50 cursor-not-allowed'
            : state === 'current'
              ? 'text-ink font-medium'
              : 'text-ink-2 hover:text-ink';
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={selected}
            onClick={() => !locked && setCurrentView(tab.id)}
            disabled={locked}
            className={`relative flex-1 sm:flex-none px-2.5 sm:px-3.5 py-2.5 text-meta sm:text-body flex items-center justify-center gap-1 whitespace-nowrap transition-colors ${tone} ${
              selected ? 'after:absolute after:left-2 after:right-2 after:-bottom-px after:h-0.5 after:bg-accent after:rounded-full' : ''
            }`}
            title={
              locked ? 'Locked until the class reaches this step'
                : state === 'done' ? 'Completed, revealed'
                  : state === 'current' ? 'The class is working on this step' : undefined
            }
          >
            {locked && <Lock className="w-3 h-3 flex-shrink-0" />}
            {!locked && state === 'done' && <Check className="w-3.5 h-3.5 flex-shrink-0 text-ink-2" />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  const actionButton = action && (
    <button
      onClick={action.onClick}
      className={`${actionQuiet ? ui.btnOutline : ui.btnPrimary} flex-shrink-0`}
    >
      <action.icon className="w-4 h-4" />{action.label}
    </button>
  );

  const facilitatorMenu = (
    <>
      {/* The variant NAMES the answer, so it lives in here, never on the
          panel, which is usually on a projector. */}
      {caseFlow.active && caseFlow.stream && (
        <p className="px-3.5 py-2 text-meta text-ink-2 border-b border-line flex items-start gap-2">
          <Shuffle className="w-3.5 h-3.5 flex-shrink-0 mt-px text-accent" />
          <span>
            Running variant {caseFlow.stream.position} of {caseFlow.stream.total}:{' '}
            <span className="font-medium text-ink">{caseFlow.stream.label}</span>
          </span>
        </p>
      )}
      <MenuItem icon={PlayCircle} label="Load case…" onClick={closeAnd(onOpenLoadCase)} />
      <MenuItem icon={BookOpen} label="Case library…" onClick={closeAnd(onOpenCaseLibrary)} />
      {caseFlow.active && <MenuItem icon={RotateCcw} label="Reset case…" onClick={closeAnd(onResetCase)} />}
      {caseFlow.active && <MenuItem icon={XCircle} label="End case" onClick={closeAnd(onEndCase)} />}
      <MenuItem icon={Settings} label="Session settings…" onClick={closeAnd(onOpenSettings)} />
      <MenuItem
        icon={Monitor}
        label={`Presenter mode: ${presenter ? 'on' : 'off'}`}
        onClick={closeAnd(onTogglePresenter)}
      />
      <MenuItem icon={Camera} label="Take snapshot" onClick={closeAnd(onSnapshot)} />
      <MenuItem icon={ClipboardCheck} label="Must-not-miss checklist…" onClick={closeAnd(onOpenChecklist)} />
      <MenuItem icon={BarChart3} label="Participation…" onClick={closeAnd(onOpenAnalytics)} />
      <MenuItem icon={Archive} label="End & archive…" onClick={closeAnd(onOpenArchive)} />
    </>
  );

  const addRowItems = !spectator && currentView === 'cdm' ? (
    <>
      <MenuItem icon={Plus} label="Add Regular Row" onClick={closeAnd(onAddRow)} />
      <MenuItem icon={Plus} label="Add Critical Dx" onClick={closeAnd(onAddCriticalRow)} tone="text-flag-red" />
    </>
  ) : null;

  const sessionGroup = (
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-meta text-ink-2 min-w-0">
      <span className="truncate">
        <span className="text-ink">{userName}</span>
        {isFacilitator && <span className={`${ui.chip} ml-1.5`}><GraduationCap className="w-3 h-3" />Facilitator</span>}
        {spectator && <span className={`${ui.chip} ml-1.5`}><Eye className="w-3 h-3" />Viewing</span>}
      </span>
      <span className="hidden sm:inline">·</span>
      {/* Visually hidden on a phone (the bar is app name + online count),
          but still read out, and still in the page text. */}
      <span className="sr-only sm:not-sr-only sm:flex items-center gap-1">
        <Users className="w-3.5 h-3.5" />
        Session: <span className="font-medium text-accent">{sessionId}</span>
      </span>
      {onlineCount > 0 && (
        <span className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-accent' : 'bg-flag-red'}`} title={connected ? 'Live sync connected' : 'Connection lost, changes will not save'} />
          {onlineCount} online
        </span>
      )}
      {!connected && <span className="text-flag-red font-medium">offline, reconnecting…</span>}
      {budget && (
        <span className={budget.spent >= budget.total ? 'text-flag-red font-medium' : 'text-ink'} title="Vote budget for this session">
          Votes: {budget.spent}/{budget.total}
        </span>
      )}
    </span>
  );

  const showBar = !collapsed && !presenter;

  return (
    /* Sticky: the bar, the tabs and the case card beneath them stay in view.
       Full-bleed against the page background, so nothing scrolls under it. */
    <div className="sticky top-0 z-40 bg-page -mx-4 sm:-mx-6 px-4 sm:px-6 mb-section" ref={menuRef}>
      {showBar && (
        <div className="h-12 flex items-center gap-3">
          <h1 className="text-heading font-semibold text-ink flex-shrink-0">CDM Grid</h1>
          {sessionGroup}

          {/* Desktop actions: text buttons, one outline, one filled. */}
          <div className="hidden sm:flex items-center gap-1 ml-auto flex-shrink-0">
            <button onClick={onInvite} className={ui.btnText}>
              <QrCode className="w-4 h-4" />Invite
            </button>
            <div className="relative">
              <button onClick={() => toggle('export')} className={ui.btnText}>
                <Download className="w-4 h-4" />Export<ChevronDown className="w-3.5 h-3.5 text-ink-2" />
              </button>
              <Dropdown open={openMenu === 'export'}>
                <MenuItem icon={FileText} label="PDF (print view)" onClick={closeAnd(onExportPdf)} />
                <MenuItem icon={Table} label="CSV (spreadsheet)" onClick={closeAnd(onExportCsv)} />
              </Dropdown>
            </div>
            {addRowItems && (
              <div className="relative">
                <button onClick={() => toggle('add')} className={ui.btnText}>
                  <Plus className="w-4 h-4" />Add Row<ChevronDown className="w-3.5 h-3.5 text-ink-2" />
                </button>
                <Dropdown open={openMenu === 'add'}>{addRowItems}</Dropdown>
              </div>
            )}
            {!spectator && currentView === 'differential' && (
              <button onClick={onAddDiffRow} className={ui.btnText}>
                <Plus className="w-4 h-4" />Add Row
              </button>
            )}
            {isFacilitator && (
              <div className="relative">
                <button onClick={() => toggle('facilitator')} className={ui.btnOutline}>
                  <GraduationCap className="w-4 h-4" />Facilitator<ChevronDown className="w-3.5 h-3.5 text-ink-2" />
                </button>
                <Dropdown open={openMenu === 'facilitator'}>{facilitatorMenu}</Dropdown>
              </div>
            )}
            <button onClick={onLeave} className={ui.iconBtn} aria-label="Leave session" title="Leave session">
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {/* Phone: everything behind one overflow menu. */}
          <div className="sm:hidden relative ml-auto flex items-center gap-1">
            <button onClick={() => toggle('more')} className={ui.iconBtn} aria-label="More actions">
              <MoreHorizontal className="w-5 h-5" />
            </button>
            <Dropdown open={openMenu === 'more'}>
              <MenuItem icon={QrCode} label="Invite" onClick={closeAnd(onInvite)} />
              <MenuItem icon={FileText} label="Export PDF (print view)" onClick={closeAnd(onExportPdf)} />
              <MenuItem icon={Table} label="Export CSV (spreadsheet)" onClick={closeAnd(onExportCsv)} />
              {addRowItems}
              {!spectator && currentView === 'differential' && <MenuItem icon={Plus} label="Add Row" onClick={closeAnd(onAddDiffRow)} />}
              {isFacilitator && <div className="my-1 border-t border-line" />}
              {isFacilitator && facilitatorMenu}
              <div className="my-1 border-t border-line" />
              <MenuItem icon={LogOut} label="Leave session" onClick={closeAnd(onLeave)} />
            </Dropdown>
          </div>

          {/* One collapse button for both layouts (a phone must not get a
              hidden desktop twin first in the DOM), then the one filled action. */}
          <button
            onClick={onToggleCollapse}
            className={`${ui.iconBtn} flex-shrink-0`}
            aria-label="Collapse header"
            aria-expanded="true"
            title="Collapse, keep the tabs and the case only"
          >
            <ChevronsUp className="w-4 h-4" />
          </button>
          {actionButton && <div className="hidden sm:block ml-2">{actionButton}</div>}
        </div>
      )}

      {/* Navigation AND progress: one strip. The action joins it when the bar is hidden. */}
      <div className="flex items-center gap-2 border-b border-line">
        {tabs}
        {!showBar && (
          <>
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${connected ? 'bg-accent' : 'bg-flag-red'}`}
              title={connected ? 'Live sync connected' : 'Connection lost, changes will not save'}
            />
            {!presenter && (
              <button
                onClick={onToggleCollapse}
                className={ui.iconBtn}
                aria-label="Expand header"
                aria-expanded="false"
                title="Show session details and actions"
              >
                <ChevronsDown className="w-4 h-4" />
              </button>
            )}
            {actionButton && <div className="hidden sm:block py-1">{actionButton}</div>}
          </>
        )}
      </div>
      {/* Phone (and hidden-bar) placement of the one action. */}
      {actionButton && (showBar ? (
        <div className="sm:hidden py-2 [&>button]:w-full">{actionButton}</div>
      ) : (
        <div className="sm:hidden py-2 [&>button]:w-full">{actionButton}</div>
      ))}

      {/* The guided case: title, problem representation, diagnosis banner. */}
      {children}
    </div>
  );
}
